import { Menu, Moon, ShieldCheck, Sun, X } from "lucide-react";
import { Suspense, useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";

import { useAttendanceStream, useMe } from "@/api/queries";
import { useUser } from "@/auth/AuthProvider";
import { ROLE_LABEL } from "@/auth/roles";
import { useTheme } from "@/lib/theme";
import { ErrorBoundary, IconButton, Logo, OrganisationBadge, Person, SkeletonText, SkipLink } from "@/ui";

import s from "./AppShell.module.css";
import { navFor } from "./nav";
import type { NavGroup, NavItem } from "./nav";
import { SignOutButton } from "./SignOutButton";

/** The screen width under which the sidebar becomes a top bar and a drawer (the same as the stylesheet). */
const NARROW = "(max-width: 860px)";

/** The destinations a role has, in the order shown: ungrouped first, then each group under its heading. */
function sections(items: NavItem[]): Array<{ group?: NavGroup; items: NavItem[] }> {
  const out: Array<{ group?: NavGroup; items: NavItem[] }> = [];
  for (const item of items) {
    const last = out[out.length - 1];
    if (last && last.group === item.group) last.items.push(item);
    else out.push({ group: item.group, items: [item] });
  }
  return out;
}

/** The destinations as a list of links with their group headings: the sidebar on a wide screen, the drawer on a narrow one. */
function NavLinks({ items, idPrefix, onNavigate }: { items: NavItem[]; idPrefix: string; onNavigate?: () => void }) {
  return (
    <>
      {sections(items).map(({ group, items: links }) => (
        <div key={group ?? "top"} className={s.navGroup}>
          {group ? <h2 className={s.navHeading} id={`${idPrefix}-${group}`}>{group}</h2> : null}
          <ul className={s.navList} aria-labelledby={group ? `${idPrefix}-${group}` : undefined}>
            {links.map(({ to, label, icon: Icon }) => (
              <li key={to}>
                <NavLink to={to} className={({ isActive }) => (isActive ? `${s.link} ${s.active}` : s.link)} onClick={onNavigate}>
                  <Icon aria-hidden />
                  {label}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </>
  );
}

export function AppShell() {
  const user = useUser();
  const { theme, toggle } = useTheme();
  const me = useMe();
  // A page shows for the roles it lists, and for anyone whose job role reaches it (Who sees whom).
  const items = navFor(user.role, me.data?.access);
  // Changes to who is working show as they happen, on every page: one stream per open console.
  useAttendanceStream(user.role !== "super_admin");
  const { pathname } = useLocation();
  // Platform admins belong to no organisation; everyone else sees whose console this is.
  const organisation = user.role === "super_admin" ? "Platform administration" : me.data?.tenant?.name;
  const logoVersion = user.role === "super_admin" ? undefined : me.data?.tenant?.logo_version;

  // The drawer on narrow screens: a native dialog, so Escape, the focus trap and the inert page come
  // from the browser. It closes when a destination is chosen, and when the screen grows wide.
  const [menuOpen, setMenuOpen] = useState(false);
  const drawer = useRef<HTMLDialogElement>(null);
  const menuButton = useRef<HTMLButtonElement>(null);
  const closeMenu = () => setMenuOpen(false);
  useEffect(() => {
    const dialog = drawer.current;
    if (!dialog) return;
    if (menuOpen && !dialog.open) {
      dialog.showModal();
      // The first destination, not the Close button, is where reading starts.
      dialog.querySelector<HTMLElement>("nav a")?.focus();
    }
    if (!menuOpen && dialog.open) dialog.close();
  }, [menuOpen]);
  useEffect(() => {
    const media = window.matchMedia?.(NARROW);
    if (!media) return;
    const onChange = () => {
      if (!media.matches) setMenuOpen(false);
    };
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  const themeButton = (
    <IconButton size="small" tooltip="left" onClick={toggle} label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}>
      {theme === "dark" ? <Sun aria-hidden /> : <Moon aria-hidden />}
    </IconButton>
  );
  const organisationBadge = organisation ? (
    <OrganisationBadge
      name={organisation}
      sub={user.role === "super_admin" ? undefined : "Organisation"}
      logoVersion={logoVersion}
      icon={user.role === "super_admin" ? <ShieldCheck aria-hidden /> : undefined}
    />
  ) : null;
  const signOutButton = <SignOutButton />;

  return (
    <div className={s.shell}>
      <SkipLink href="#main" />
      <aside className={s.sidebar}>
        <IconButton ref={menuButton} className={s.menuButton} tooltip="bottom" label="Menu" aria-expanded={menuOpen} aria-controls="app-drawer" onClick={() => setMenuOpen(true)}>
          <Menu aria-hidden />
        </IconButton>
        <div className={s.brand}>
          <Logo height={26} />
          {/* The sidebar shows the card; the phone's top bar keeps to the name. */}
          <div className={s.organisationCard}>{organisationBadge}</div>
          {organisation ? <p className={s.organisation} title={organisation}>{organisation}</p> : null}
        </div>
        <nav className={s.nav} aria-label="Main">
          <NavLinks items={items} idPrefix="nav" />
        </nav>
        <p className={s.version}>Zotula <span className="num">{__APP_VERSION__}</span></p>
        <div className={s.account}>
          <Link to="/profile" className={s.profile}>
            <span className="sr-only">Your profile: </span>
            <Person name={user.name} sub={ROLE_LABEL[user.role]} userId={user.id} pictureVersion={user.picture_version} />
          </Link>
          {/* Tooltips open to the left: the row is at the foot of the sidebar, or at the top of a phone's screen. */}
          <div className={s.accountActions}>
            {themeButton}
            {signOutButton}
          </div>
        </div>
      </aside>

      <dialog
        ref={drawer}
        id="app-drawer"
        className={s.drawer}
        aria-label="Menu"
        onClose={() => setMenuOpen(false)}
        onCancel={() => setMenuOpen(false)}
        // A press on the dark backdrop (the dialog element itself, outside its panel) closes it.
        onClick={(event) => {
          if (event.target === event.currentTarget) setMenuOpen(false);
        }}
      >
        {menuOpen ? (
          <div className={s.drawerPanel}>
            <div className={s.drawerHead}>
              <Logo height={24} />
              <IconButton label="Close menu" tooltip="left" onClick={() => setMenuOpen(false)}><X aria-hidden /></IconButton>
            </div>
            <div className={s.drawerOrganisation}>{organisationBadge}</div>
            <nav className={s.drawerNav} aria-label="Main">
              <NavLinks items={items} idPrefix="drawer" onNavigate={closeMenu} />
            </nav>
            <div className={s.drawerFoot}>
              <Link to="/profile" className={s.profile} onClick={closeMenu}>
                <span className="sr-only">Your profile: </span>
                <Person name={user.name} sub={ROLE_LABEL[user.role]} userId={user.id} pictureVersion={user.picture_version} />
              </Link>
              <div className={s.accountActions}>
                {signOutButton}
              </div>
            </div>
            <p className={s.version}>Zotula <span className="num">{__APP_VERSION__}</span></p>
          </div>
        ) : null}
      </dialog>

      <main className={s.main} id="main" tabIndex={-1}>
        <div className={s.content}>
          {/* A page that is still downloading, or fails to, keeps the navigation around it. */}
          <ErrorBoundary resetKey={pathname}>
            <Suspense fallback={<SkeletonText lines={4} />}>
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </div>
      </main>
    </div>
  );
}
